import { useCallback, useSyncExternalStore } from 'react';
import {
  getCorner,
  getCornerSignal,
  onCorner,
  onCornerSignal,
  otherCorner,
  setCorner,
  type Corner,
} from '../lib/corner';

/**
 * Whether the corner cluster is folded away, as React sees it.
 *
 * Like `useTheme` and unlike `useLocale`, this needs no provider. The decision
 * lives in lib/corner.ts — a module, for the reasons that file gives — and
 * almost nothing in the tree has to *read* it: the state is an attribute on
 * <html> and the cluster follows it through CSS. There are two readers. The
 * fold itself, which has to say which way it will move and whether anything
 * is waiting behind it; and the chat dock, which unfolds the corner when its
 * panel opens and closes the panel when the corner folds, because a panel
 * whose bubble is off screen is a door with no handle.
 *
 * `useSyncExternalStore` rather than a context and an effect, because that is
 * exactly what this is: two values owned outside React, each with a subscribe
 * and a read. Both snapshots are the same read on the server as on the
 * client — there is no document in the screenshot harness, lib/corner answers
 * "shown" there, and pinning a constant here would only make that answer
 * unreachable from a render that had set it.
 */
export interface CornerValue {
  corner: Corner;
  /** True when the cluster is tucked into the edge. */
  folded: boolean;
  /** True when something behind the fold wants attention — chat, in practice. */
  signal: boolean;
  setCorner(corner: Corner): void;
  toggle(): void;
}

export function useCorner(): CornerValue {
  const corner = useSyncExternalStore(onCorner, getCorner, getCorner);
  const signal = useSyncExternalStore(onCornerSignal, getCornerSignal, getCornerSignal);
  const toggle = useCallback(() => setCorner(otherCorner()), []);
  return { corner, folded: corner === 'folded', signal, setCorner, toggle };
}
